/**
 * HomePage — C-1. 스튜디오 홈(메인화면)
 *
 * 섹션 구성 (기획서 C-0 순서 반영 — 오늘의 현장 상황을 작업 가이드보다 위로):
 *   1) 메인 타이틀 — 인사문구만 (추천 카드 배너는 제거)
 *   2) 오늘의 현장 상황, 빠르게 확인해볼까요? — 접힘형 타이틀(CollapsibleSection) + 활성 현장(DONE 제외, 최대 10개) 카드.
 *      카드 내용 4종(오늘 작업 공정/오늘 발주 공정/최근2일 현장보고/최근2일 중요 알림)은 siteId 배치 API 3종을
 *      각 1회씩만 호출해 채운다(스튜디오홈_현장카드_플랫폼공통API_요청.md). 인디플래너 site-today가 아직
 *      미구현이면 작업공정/발주공정은 안내 placeholder로 폴백한다.
 *   3) 오늘은 어떤 일부터 시작해볼까요? — 동일한 접힘형 타이틀 + 정적 작업 가이드 카드 3개.
 *      하위 항목은 텍스트 목록이 아니라 아이콘 그리드(0907 기획서 개편) — 아이콘 클릭 시 실제 화면 라우팅,
 *      호버 시 위 안내줄에 해당 항목 설명 표시(TaskIconGrid).
 *
 * 기존 "처리할 작업"(brief)·"최근 작업"·"내 에이전트" 섹션은 기획서 재구성으로 제거.
 */
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@indipia/shared';
import type { BadgeStatus } from '@indipia/ui-core';
import {
  Alert, Badge, Button, Card, CardList, Checkbox, CollapsibleSection,
  EmptyState, ErrorState, IconCheckCircle, IconInfoCircle, IconWarningCircle, Modal, Page, PageHeader, Skeleton, Tooltip,
} from '@indipia/ui-core';
import { SiteRegistrationPopup, useMineOnlyState } from '@indipia/shared-features';
import { fetchGreeting } from '../api/greeting';
import { fetchSiteList, fetchSiteActivities } from '../api/sites';
import type { SiteListItem, SiteActivityItem } from '../api/sites';
import { fetchSiteTodays } from '../api/siteToday';
import type { SiteTodayContent, SiteTodayProcessItem, SiteTodayOrderItem } from '../api/siteToday';
import { fetchImportantNotifications } from '../api/notifications';
import type { NotifItem } from '../api/notifications';
import { SiteDetailPanel } from '../../app/shell/SiteDetailPanel';
import { groupBy } from '../lib/group-by';
import { useMenuTree, useAllMenuTree, resolveMenuCodeAction, findByCode, type MenuNode } from '../../registry/menu-tree';
import { showInfoToast } from '../../app/shell/notifications/InfoToast';
import { TodayFocusBanner } from '../todo/TodayFocusBanner';

const APP_ONLY_MESSAGE = '앱 전용 화면입니다. 인디피아 앱을 이용해주세요.';

/** 스튜디오 홈 to-be(940 S14-1) — '오늘의 현장 상황' 영역은 화면에서 숨긴다(정의·모달·쿼리 코드는 보존, 재노출 시 이 플래그만 올린다).
 *  false 인 동안 이 영역 전용 조회(현장 목록·오늘공정·현장보고·중요알림)도 호출하지 않는다. */
const SHOW_SITE_STATUS = false;

/** TM_AGENT.AGENT_ID — 인디공사현황(A02, work-log). "최근2일 현장보고"의 고정 조회 대상. */
const WORK_LOG_AGENT_ID = 2;

// ── 오늘은 어떤 일부터 시작해볼까요? — 정적 작업 가이드 (0803 기획서 §2 그대로) ──────
// 하위 항목 경로는 각 에이전트팀 확인 완료(인디공사현황: 정남교 유닛장님).
// customer-reports/settings 는 아직 미개발 화면 — 라우팅만 연결, 도착화면은 해당 에이전트팀 책임.

interface HomeTaskSubItem {
  text: string;
  /** TM_MENU(WEB_PHOTO/WEB_SCHEDULE) MENU_CODE — 있으면 이 코드의 ROUTE_PATH를 우선 사용한다. */
  menuCode?: string;
  /** TM_MENU 조회 실패·미시딩 시 폴백 경로(기존 하드코딩 값 그대로 유지). */
  path: string;
  /** 기획서 0907 개편 — 아이콘 그리드용 아이콘(28x28). */
  icon: React.ReactNode;
  /** 아이콘 아래 2줄 라벨 — [1행, 2행]. */
  shortLabel: [string, string];
}

interface HomeTask {
  agentId: 'photo-organizer' | 'scheduler' | 'work-log';
  label: string;
  group: string; // data-group 스코프값 (D-08 §6.4 groupTheme) — 카드 번호 배지 강조색으로만 사용
  subItems: HomeTaskSubItem[];
  /** true면 화면에서 숨긴다(정의는 보존 — 재노출 시 이 플래그만 내린다). */
  hidden?: boolean;
}

/** 아이콘 그리드 하위 항목 아이콘 — 56px 플레이트 안에 들어가는 28x28(stroke 공통 속성). */
function SubIcon({ children }: { children: React.ReactNode }) {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  );
}

const SHARE_ICON = (
  <SubIcon>
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </SubIcon>
);

const HOME_TASKS: HomeTask[] = [
  {
    agentId: 'photo-organizer',
    label: '현장 사진 정리하고, 고객과 공유하기',
    group: '01',
    subItems: [
      {
        text: '내가 찍은 현장 사진 정리해요', menuCode: 'WEB_PHOTO_ENTRY_MOBILE', path: '/agents/photo-organizer/upload?entry=MOBILE',
        shortLabel: ['내 사진', '정리'],
        icon: <SubIcon><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></SubIcon>,
      },
      {
        text: '고객에게 받은 사진 정리할까요', menuCode: 'WEB_PHOTO_ENTRY_CUSTOMER', path: '/agents/photo-organizer/upload?entry=CUSTOMER',
        shortLabel: ['고객 사진', '정리'],
        icon: <SubIcon><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 4-7 8-7s8 3 8 7" /></SubIcon>,
      },
      {
        text: '시공반장에게 받은 사진도 정리할까요', menuCode: 'WEB_PHOTO_ENTRY_PARTNER', path: '/agents/photo-organizer/upload?entry=PARTNER',
        shortLabel: ['시공반장', '사진 정리'],
        icon: <SubIcon><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></SubIcon>,
      },
      {
        text: '컴퓨터에 저장된 사진도 정리해봐요', menuCode: 'WEB_PHOTO_ENTRY_PC', path: '/agents/photo-organizer/upload?entry=PC',
        shortLabel: ['컴퓨터', '사진 정리'],
        icon: <SubIcon><rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></SubIcon>,
      },
      {
        text: '정리한 사진들을 필요한 사람들과 공유해요', menuCode: 'WEB_PHOTO_SHARE', path: '/agents/photo-organizer/share',
        shortLabel: ['정리한 사진', '공유'],
        icon: SHARE_ICON,
      },
      {
        text: '정리한 사진들로 시공사례 만들어요', menuCode: 'WEB_PHOTO_PORTFOLIO_WRITE', path: '/agents/portfolio/write',
        shortLabel: ['시공사례', '만들기'],
        icon: <SubIcon><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /><line x1="12" y1="11" x2="12" y2="17" /><line x1="9" y1="14" x2="15" y2="14" /></SubIcon>,
      },
      {
        text: '만든 시공사례, 블로그로도 퍼가봐요', menuCode: 'WEB_PHOTO_PORTFOLIO_SNS', path: '/agents/portfolio/sns',
        shortLabel: ['블로그로', '퍼가기'],
        icon: <SubIcon><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></SubIcon>,
      },
    ],
  },
  {
    agentId: 'scheduler',
    label: '공사 스케줄 짜고, 조정하기',
    group: '03',
    subItems: [
      {
        text: '내 견적서로 스케줄 자동으로 만들어요', menuCode: 'WEB_SCHEDULE_CREATE_EXCEL', path: '/agents/scheduler?view=create-site&entry=QUOTE',
        shortLabel: ['스케줄', '자동 생성'],
        icon: <SubIcon><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></SubIcon>,
      },
      {
        text: '견적서 없이 스케줄을 직접 만들어요', menuCode: 'WEB_SCHEDULE_CREATE_CUST', path: '/agents/scheduler?view=create-site&entry=MANUAL',
        shortLabel: ['스케줄', '직접 생성'],
        icon: <SubIcon><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /><line x1="12" y1="14" x2="12" y2="18" /><line x1="10" y1="16" x2="14" y2="16" /></SubIcon>,
      },
      {
        text: '현장 상황따라 스케줄 맘대로 조정해요', menuCode: 'WEB_SCHEDULE_GANTT', path: '/agents/scheduler?view=gantt',
        shortLabel: ['스케줄', '조정'],
        icon: <SubIcon><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16.5 14.5" /></SubIcon>,
      },
      {
        text: '조정한 스케줄, 고객·작업반장과 공유해요', menuCode: 'WEB_SCHEDULE_KAKAO_SHARE', path: '/agents/scheduler?view=kakao',
        shortLabel: ['스케줄', '공유'],
        icon: SHARE_ICON,
      },
      {
        text: '진행중 모든 현장 일정, 한눈에 확인해요', menuCode: 'WEB_SCHEDULE_CALENDAR', path: '/agents/scheduler?view=calendar',
        shortLabel: ['모든 일정', '확인'],
        icon: <SubIcon><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /><path d="M9 16l2 2 4-4" /></SubIcon>,
      },
    ],
  },
  {
    agentId: 'work-log',
    label: '현장 작업요청/보고확인하고, 고객과 공유',
    group: '02',
    subItems: [
      {
        text: '시공 파트너에게 작업 요청해요', menuCode: 'WORKORDER_WRITE', path: '/agents/work-log/workorder/new',
        shortLabel: ['작업요청', '보내기'],
        icon: <SubIcon><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></SubIcon>,
      },
      {
        text: '내가 직접 작업 보고 작성해요', menuCode: 'FIELD_REPORTS_WRITE', path: '/agents/work-log/field-reports/new',
        shortLabel: ['작업보고', '작성'],
        icon: <SubIcon><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4Z" /></SubIcon>,
      },
      {
        text: '현장 작업자 작업 보고를 확인해 봐요', menuCode: 'FIELD_REPORTS_LIST', path: '/agents/work-log/field-reports',
        shortLabel: ['작업보고', '확인'],
        icon: <SubIcon><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></SubIcon>,
      },
      {
        text: '현장 전체 공사 현황을 한눈에 파악해요', menuCode: 'SITE_REPORTS', path: '/agents/work-log/site-reports',
        shortLabel: ['공사현황', '파악'],
        icon: <SubIcon><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /></SubIcon>,
      },
      {
        // customer-reports/settings(CustomerReportSettingPage)는 폐기되고 customer-reports/edit로 통합됨(설계서 §5.6).
        text: '작업 보고 편집-고객에게 바로 공유해요', menuCode: 'CUSTOMER_REPORTS', path: '/agents/work-log/customer-reports/edit',
        shortLabel: ['고객리포트', '공유'],
        icon: SHARE_ICON,
      },
    ],
  },
];

// ── 오늘의 현장 상황 카드 — 4개 섹션 문구 조립 ──────────────────────────────────
// 오늘 작업공정/발주공정은 인디플래너 site-today 실시간 조회(§6) 결과를 조립한다.
// 인디플래너가 아직 /site-today를 구현하기 전에는 content가 항상 null이라 안내 placeholder로 폴백한다.

// 데이터 없을 때 보여줄 안내문구 — "채워지면 이렇게 보여요"를 짧은 문장으로 설명한다.
// 카드 안 한 줄짜리 부분 빈 상태라 상태 블록(§15.37)이 아니라 인라인 안내다(D-07 §17.2).
// 색·크기는 상태 블록 설명문과 같은 토큰(caption 12 / shell.onSurface2)을 쓴다 — 앱
// studio_screen `_DetailSection.emptyText` 와 동일. StatusHint(§15.34)는 11px·nowrap
// 말줄임이라 이 길이의 문장을 잘라먹어 쓰지 않는다.
// `!` 는 `.app-card p { font-size: 12.5px }`(components.css) 를 이기기 위한 것이다 —
// 그 12.5 는 D-07 §9 스케일에 없는 값이라 별건으로 정리 대상이고, 여기서 전역을 건드리면
// 앱 전체 카드 본문이 같이 줄어든다. 아래 !leading-tight 도 같은 이유의 로컬 우선순위다.
const EMPTY_GUIDE_CLASS = '!text-caption text-text-secondary';
const PROCESS_GUIDE = '스케줄 등록 시 오늘 공정을 알려드려요';
const ORDER_GUIDE = '발주정보 등록 시 발주 타이밍을 알려드려요';
const ACTIVITY_GUIDE = '작업요청 시 현장보고를 확인할 수 있어요';
const NOTI_GUIDE = '알림톡 발송 시 중요 알림을 챙겨드려요';

// 카드 본문 한 줄 = 높이 24 고정(min/max 6 = 1.5rem). 실데이터 항목과 빈 상태 안내문이 같은
// 높이를 써야 현장카드 3장의 섹션 위치가 어긋나지 않는다.
// `!leading-6` 의 `!` 는 EMPTY_GUIDE_CLASS 의 `!text-caption`(line-height 100% important)을 이기기 위한 것.
// items-center 는 행 안에 아이콘·배지 등 24보다 큰 요소가 섞여도 글자가 세로 가운데에 오게 한다.
const ITEM_CLASS = 'flex min-h-6 max-h-6 items-center overflow-hidden !leading-6';

// 섹션 제목 — 카드 제목(h3) 아래 단계라 h4. `.app-card h4 { margin:8px 0 4px; font-size:15px; font-weight:600 }`
// (components.css)가 특이도 (0,1,1)로 유틸리티를 이기므로 size·weight·margin 세 가지를 `!`로 되돌린다.
const SECTION_TITLE = '!m-0 !text-heading-sm !font-bold text-text';

/** 며칠차가 같은 공정끼리는 한 줄로 묶는다("설비(배관), 전기(배선) 1일차 진행중") — 카드 줄 수를 줄이기 위함. */
function groupProcessLines(processes: SiteTodayProcessItem[]): string[] {
  const groups = new Map<number | null, string[]>();
  for (const p of processes) {
    const key = p.processDayNumber;
    const titles = groups.get(key) ?? [];
    titles.push(p.processTitle);
    groups.set(key, titles);
  }
  return Array.from(groups.entries()).map(([day, titles]) => {
    const suffix = day != null ? ` ${day}일차 진행중` : ' 진행중';
    return `${titles.join(', ')}${suffix}`;
  });
}

/** 거래처는 시안에서 아래 Caption 한 줄로 분리됐다(3836:1851) — 본문 줄에는 넣지 않는다. */
function formatOrderLine(item: SiteTodayOrderItem): string {
  const due = item.orderDueInDays === 0
    ? '오늘 시공'
    : item.orderDueInDays === 1
      ? '내일 시공'
      : item.orderDueInDays != null
        ? `${item.orderDueInDays}일 후 시공`
        : null;
  return `${item.orderMaterialName} 자재 발주 필요${due ? ` · ${due}` : ''}`;
}

// ── 현장보고·중요알림 행 ─────────────────────────────────────────────────

/** 시안 "6/25 07:40". 레포에 공용 날짜 유틸이 없어 지역 함수로 둔다(두 번째 소비처가 생기면 shared/lib 로). */
function formatStamp(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 시안의 [분류][본문][담당자] 3조각으로 쪼갠다.
 *  ' · ' 구분은 API 계약이 아니라 발행 에이전트의 payload 관례다 — BE는 payload.text 를
 *  해석하지 않고 통과시킨다(indipia-api-spec §activity-logs/site). 그래서 어긋나면 분해를
 *  포기하고 통문장을 본문에 넣는다. 잘못 쪼개 글자를 잃는 쪽이 더 나쁘다.
 *  알림(NotifItem.body)은 hermes 템플릿 문장이라 구분자 관례가 없어 대부분 폴백으로 떨어진다. */
function splitRow(s: string): { body: string; who: string | null } {
  const parts = s.split(' · ');
  if (parts.length < 3) return { body: s, who: null };
  return { body: parts.slice(1, -1).join(' · '), who: parts[parts.length - 1] };
}

type RowTone = { label: string; status: BadgeStatus; Icon: typeof IconCheckCircle; color: string };

/** 판정 리터럴은 countIssues 와 같은 값을 쓴다 — 한쪽만 고치면 헤더 개수와 행 배지가 조용히 어긋난다. */
function activityTone(text: string | null): RowTone {
  return text?.includes('이슈사항')
    ? { label: '이슈발생', status: 'warning', Icon: IconWarningCircle, color: 'text-warning-600' }
    : { label: '정상', status: 'success', Icon: IconCheckCircle, color: 'text-success-600' };
}

/** 일정확인 회신 결과 알림 5종(FR-D-55)은 본문이 아니라 알림 종류(notiType)로 판정한다(FR-D-80).
 *  불가 회신·확정 처리는 이슈 건수에 세지 않는다(본문 '일정불가능' 우연 일치 방지).
 *  그 외 알림은 본문의 "일정불가능" 포함 여부로 판정한다(기존 규칙 유지). */
function isIssueNoti(n: Pick<NotifItem, 'notiType' | 'body'>): boolean {
  if (n.notiType === 'CREW_CONFIRM_UNFIXED') return true;
  if (
    n.notiType === 'CREW_CONFIRM_ACCEPTED' || n.notiType === 'CREW_CONFIRM_FORWARDED'
    || n.notiType === 'CREW_CONFIRM_DECLINED' || n.notiType === 'CREW_CONFIRM_FIXED'
  ) return false;
  return !!n.body?.includes('일정불가능');
}

function notiTone(n: Pick<NotifItem, 'notiType' | 'body'>): RowTone {
  switch (n.notiType) {
    case 'CREW_CONFIRM_UNFIXED':
      return { label: '미확정', status: 'warning', Icon: IconWarningCircle, color: 'text-warning-600' };
    case 'CREW_CONFIRM_FORWARDED':
      return { label: '다음 순위 발송', status: 'info', Icon: IconCheckCircle, color: 'text-info-600' };
    case 'CREW_CONFIRM_ACCEPTED':
      return { label: '일정가능', status: 'success', Icon: IconCheckCircle, color: 'text-success-600' };
    case 'CREW_CONFIRM_DECLINED':
      return { label: '일정불가', status: 'danger', Icon: IconWarningCircle, color: 'text-danger-600' };
    case 'CREW_CONFIRM_FIXED':
      return { label: '확정 처리', status: 'success', Icon: IconCheckCircle, color: 'text-success-600' };
    default:
      return isIssueNoti(n)
        ? { label: '일정불가', status: 'danger', Icon: IconWarningCircle, color: 'text-danger-600' }
        : { label: '일정가능', status: 'success', Icon: IconCheckCircle, color: 'text-success-600' };
  }
}

/** 카드·더보기 모달 공용 — 이슈 건수 합산. 알림 판정은 notiTone 과 같은 isIssueNoti 를 쓴다. */
function countIssues(activities: SiteActivityItem[], notis: NotifItem[]): number {
  return activities.filter((a) => a.text?.includes('이슈사항')).length
    + notis.filter(isIssueNoti).length;
}

/** 카드·더보기 모달 공용 — "이슈 N건" 배지. 0건도 그린다(카드 3장 높이를 맞추기 위함). */
function IssueSummary({ issueCount }: { issueCount: number }) {
  return (
    <div className="mt-3">
      <Badge status={issueCount > 0 ? 'warning' : 'mute'}>이슈 {issueCount}건</Badge>
    </div>
  );
}

// ── 현장카드 4섹션 렌더링 — 카드 본문(limit=1)과 "더보기" 모달(limit 없음)이 공용으로 쓴다 ──

function SiteStatusSections({
  today,
  activities,
  notis,
  limit,
  navigate,
  onMoreClick,
  menuTree,
}: {
  today: SiteTodayContent | null;
  activities: SiteActivityItem[];
  notis: NotifItem[];
  limit?: number;
  navigate: (path: string) => void;
  onMoreClick?: () => void;
  menuTree: MenuNode[] | undefined;
}) {
  const shownActivities = limit != null ? activities.slice(0, limit) : activities;
  const shownNotis = limit != null ? notis.slice(0, limit) : notis;
  const processes = today?.processes ?? [];
  const orders = today?.orders ?? [];
  // 더보기는 카드 하단 우측 1개로 통합한다(시안 3836:1874). limit이 없는 더보기 모달에서는
  // 0이 되어 버튼이 렌더되지 않는다 — 별도 prop 없이 모달 분기가 처리된다.
  const hidden = limit == null
    ? 0
    : Math.max(0, activities.length - limit) + Math.max(0, notis.length - limit);

  return (
    <div className="flex h-full flex-col gap-4">
      <section>
        <h4 className={SECTION_TITLE}>오늘 작업 공정</h4>
        {processes.length === 0 ? (
          <p className={`mt-4 w-full ${EMPTY_GUIDE_CLASS} ${ITEM_CLASS}`}>{PROCESS_GUIDE}</p>
        ) : (
          <ul className="mt-4 space-y-0.5">
            {groupProcessLines(processes).map((line, i) => (
              <li key={i}>
                <div className={`w-full text-body-sm text-text-secondary ${ITEM_CLASS}`}>
                  {line}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="border-t border-border pt-4">
        <h4 className={SECTION_TITLE}>오늘 발주 공정</h4>
        {orders.length === 0 ? (
          <p className={`mt-4 w-full ${EMPTY_GUIDE_CLASS} ${ITEM_CLASS}`}>{ORDER_GUIDE}</p>
        ) : (
          <ul className="mt-4 space-y-2">
            {orders.map((o, i) => (
              <li key={i}>
                <div className="w-full text-body-sm text-text-secondary">{formatOrderLine(o)}</div>
                {/* 거래처는 시안에서 별도 Caption 줄이다(3836:1851) */}
                {o.orderVendorName && (
                  <div className="mt-1 text-caption text-text-secondary">거래처: {o.orderVendorName}</div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="border-t border-border pt-4">
        <h4 className={SECTION_TITLE}>최근 2일 현장보고</h4>
        {shownActivities.length === 0 ? (
          <p className={`mt-4 w-full ${EMPTY_GUIDE_CLASS} ${ITEM_CLASS}`}>{ACTIVITY_GUIDE}</p>
        ) : (
          <ul className="mt-4 space-y-2">
            {shownActivities.map((a, i) => {
              const tone = activityTone(a.text);
              const { body, who } = splitRow(a.text ?? a.activityTypeCode);
              const action = resolveMenuCodeAction(menuTree, a.webMenuCode, a.appMenuCode, a.targetId);
              return (
                <li key={i}>
                  <button
                    type="button"
                    onClick={() => {
                      if (action.kind === 'route') navigate(action.path);
                      else if (action.kind === 'other-platform') showInfoToast(APP_ONLY_MESSAGE);
                    }}
                    disabled={action.kind === 'none'}
                    className={`flex w-full items-center gap-2 text-left ${action.kind === 'route' ? 'hover:underline' : ''}`}
                  >
                    <tone.Icon className={`shrink-0 ${tone.color}`} />
                    <Badge status={tone.status} className="shrink-0">{tone.label}</Badge>
                    <span className="min-w-0 flex-1 truncate text-body-sm text-text-secondary">{body}</span>
                    {who && <span className="shrink-0 text-body-sm font-bold text-text-secondary">{who}</span>}
                  </button>
                  <div className="mt-1 text-caption text-text-secondary">{formatStamp(a.occurredDtm)}</div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="border-t border-border pt-4">
        <h4 className={SECTION_TITLE}>최근 2일 중요알림 (고객, 시공반장)</h4>
        {shownNotis.length === 0 ? (
          <p className={`mt-4 w-full ${EMPTY_GUIDE_CLASS} ${ITEM_CLASS}`}>{NOTI_GUIDE}</p>
        ) : (
          <ul className="mt-4 space-y-2">
            {shownNotis.map((n) => {
              const tone = notiTone(n);
              const { body, who } = splitRow(n.body);
              const action = resolveMenuCodeAction(menuTree, n.webMenuCode, n.appMenuCode, n.targetId, n.siteId);
              return (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => {
                      if (action.kind === 'route') navigate(action.path);
                      else if (action.kind === 'other-platform') showInfoToast(APP_ONLY_MESSAGE);
                    }}
                    disabled={action.kind === 'none'}
                    className={`flex w-full items-center gap-2 text-left ${action.kind === 'route' ? 'hover:underline' : ''}`}
                  >
                    <tone.Icon className={`shrink-0 ${tone.color}`} />
                    <Badge status={tone.status} className="shrink-0">{tone.label}</Badge>
                    <span className="min-w-0 flex-1 truncate text-body-sm text-text-secondary">{body}</span>
                    {who && <span className="shrink-0 text-body-sm font-bold text-text-secondary">{who}</span>}
                  </button>
                  <div className="mt-1 text-caption text-text-secondary">{formatStamp(n.createdAt)}</div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {hidden > 0 && (
        <Button variant="ghost" onClick={onMoreClick} className="mt-auto self-end">
          {hidden}건 더보기 →
        </Button>
      )}
    </div>
  );
}

// 상태 목록의 SSOT는 이 switch가 아니라 DB 공통코드 그룹 ZEU_SITE_STATUS다(C-07). 둘은 동기화되지 않으므로
// 모르는 값을 danger(빨강)로 떨어뜨리면 신규 상태가 추가될 때마다 경보로 오탐한다 → 중립(mute)이 기본.
function siteBadgeStatus(status: string): BadgeStatus {
  switch (status) {
    case 'ACTIVE':
    case 'CONSULT':
    case 'QUOTE':     return 'info';
    case 'PROGRESS':
    case 'CONSTRUCT': return 'success';
    case 'FAIL':      return 'danger';
    case 'DONE':      return 'mute';
    default:          return 'mute';
  }
}

// ── 할일 카드 타이틀 아이콘 — 시안 Figma DS PT8wG7aRcWg4CerPViHOI4 노드별 export.
// 채움 글리프 path 하나가 박스까지 그린다(글리프는 녹아웃) — 별도 배경 플레이트 불필요.
// LeftNav.tsx 의 AGENT_ICON(선형)과는 이제 다른 아이콘이다 — 동기화 대상 아니다.
const TASK_AGENT_ICON: Record<HomeTask['agentId'], React.ReactNode> = {
  'photo-organizer': ( // mingcute:camera-fill (3836-1959)
    <svg width="32" height="32" viewBox="0 0 32 32" fill="currentColor" fillRule="evenodd" clipRule="evenodd" aria-hidden>
      <path d="M25.3337 4C26.3945 4 27.4119 4.42143 28.1621 5.17157C28.9122 5.92172 29.3337 6.93913 29.3337 8V24C29.3337 25.0609 28.9122 26.0783 28.1621 26.8284C27.4119 27.5786 26.3945 28 25.3337 28H6.66699C5.60613 28 4.58871 27.5786 3.83857 26.8284C3.08842 26.0783 2.66699 25.0609 2.66699 24V8C2.66699 6.93913 3.08842 5.92172 3.83857 5.17157C4.58871 4.42143 5.60613 4 6.66699 4H25.3337ZM16.0003 9.33333C14.2322 9.33333 12.5365 10.0357 11.2863 11.286C10.036 12.5362 9.33366 14.2319 9.33366 16C9.33366 17.7681 10.036 19.4638 11.2863 20.714C12.5365 21.9643 14.2322 22.6667 16.0003 22.6667C17.7684 22.6667 19.4641 21.9643 20.7144 20.714C21.9646 19.4638 22.667 17.7681 22.667 16C22.667 14.2319 21.9646 12.5362 20.7144 11.286C19.4641 10.0357 17.7684 9.33333 16.0003 9.33333ZM16.0003 12C17.0612 12 18.0786 12.4214 18.8288 13.1716C19.5789 13.9217 20.0003 14.9391 20.0003 16C20.0003 17.0609 19.5789 18.0783 18.8288 18.8284C18.0786 19.5786 17.0612 20 16.0003 20C14.9395 20 13.922 19.5786 13.1719 18.8284C12.4218 18.0783 12.0003 17.0609 12.0003 16C12.0003 14.9391 12.4218 13.9217 13.1719 13.1716C13.922 12.4214 14.9395 12 16.0003 12ZM24.0003 8C23.6467 8 23.3076 8.14048 23.0575 8.39052C22.8075 8.64057 22.667 8.97971 22.667 9.33333C22.667 9.68696 22.8075 10.0261 23.0575 10.2761C23.3076 10.5262 23.6467 10.6667 24.0003 10.6667H25.3337C25.6873 10.6667 26.0264 10.5262 26.2765 10.2761C26.5265 10.0261 26.667 9.68696 26.667 9.33333C26.667 8.97971 26.5265 8.64057 26.2765 8.39052C26.0264 8.14048 25.6873 8 25.3337 8H24.0003Z" />
    </svg>
  ),
  'scheduler': ( // mingcute:calendar-month-fill (3836-2017)
    <svg width="32" height="32" viewBox="0 0 32 32" fill="currentColor" fillRule="evenodd" clipRule="evenodd" aria-hidden>
      <path d="M28 25.3333C28 26.0406 27.719 26.7189 27.219 27.219C26.7189 27.719 26.0406 28 25.3333 28H6.66667C5.95942 28 5.28115 27.719 4.78105 27.219C4.28095 26.7189 4 26.0406 4 25.3333V10.6667H28V25.3333ZM10 20C9.64638 20 9.30724 20.1405 9.05719 20.3905C8.80714 20.6406 8.66667 20.9797 8.66667 21.3333C8.66667 21.687 8.80714 22.0261 9.05719 22.2761C9.30724 22.5262 9.64638 22.6667 10 22.6667H11.3333C11.687 22.6667 12.0261 22.5262 12.2761 22.2761C12.5262 22.0261 12.6667 21.687 12.6667 21.3333C12.6667 20.9797 12.5262 20.6406 12.2761 20.3905C12.0261 20.1405 11.687 20 11.3333 20H10ZM15.3333 20C14.9797 20 14.6406 20.1405 14.3905 20.3905C14.1405 20.6406 14 20.9797 14 21.3333C14 21.687 14.1405 22.0261 14.3905 22.2761C14.6406 22.5262 14.9797 22.6667 15.3333 22.6667H16.6667C17.0203 22.6667 17.3594 22.5262 17.6095 22.2761C17.8595 22.0261 18 21.687 18 21.3333C18 20.9797 17.8595 20.6406 17.6095 20.3905C17.3594 20.1405 17.0203 20 16.6667 20H15.3333ZM10 14.6667C9.64638 14.6667 9.30724 14.8071 9.05719 15.0572C8.80714 15.3072 8.66667 15.6464 8.66667 16C8.66667 16.3536 8.80714 16.6928 9.05719 16.9428C9.30724 17.1929 9.64638 17.3333 10 17.3333H11.3333C11.687 17.3333 12.0261 17.1929 12.2761 16.9428C12.5262 16.6928 12.6667 16.3536 12.6667 16C12.6667 15.6464 12.5262 15.3072 12.2761 15.0572C12.0261 14.8071 11.687 14.6667 11.3333 14.6667H10ZM15.3333 14.6667C14.9797 14.6667 14.6406 14.8071 14.3905 15.0572C14.1405 15.3072 14 15.6464 14 16C14 16.3536 14.1405 16.6928 14.3905 16.9428C14.6406 17.1929 14.9797 17.3333 15.3333 17.3333H16.6667C17.0203 17.3333 17.3594 17.1929 17.6095 16.9428C17.8595 16.6928 18 16.3536 18 16C18 15.6464 17.8595 15.3072 17.6095 15.0572C17.3594 14.8071 17.0203 14.6667 16.6667 14.6667H15.3333ZM20.6667 14.6667C20.313 14.6667 19.9739 14.8071 19.7239 15.0572C19.4738 15.3072 19.3333 15.6464 19.3333 16C19.3333 16.3536 19.4738 16.6928 19.7239 16.9428C19.9739 17.1929 20.313 17.3333 20.6667 17.3333H22C22.3536 17.3333 22.6928 17.1929 22.9428 16.9428C23.1929 16.6928 23.3333 16.3536 23.3333 16C23.3333 15.6464 23.1929 15.3072 22.9428 15.0572C22.6928 14.8071 22.3536 14.6667 22 14.6667H20.6667ZM25.3333 4C26.0406 4 26.7189 4.28095 27.219 4.78105C27.719 5.28115 28 5.95942 28 6.66667V8H4V6.66667C4 5.95942 4.28095 5.28115 4.78105 4.78105C5.28115 4.28095 5.95942 4 6.66667 4H25.3333Z" />
    </svg>
  ),
  'work-log': ( // mingcute:checkbox-fill (3839-2176)
    <svg width="32" height="32" viewBox="0 0 32 32" fill="currentColor" fillRule="evenodd" clipRule="evenodd" aria-hidden>
      <path d="M4 6.66667C4 5.95942 4.28095 5.28115 4.78105 4.78105C5.28115 4.28095 5.95942 4 6.66667 4H25.3333C26.0406 4 26.7189 4.28095 27.219 4.78105C27.719 5.28115 28 5.95942 28 6.66667V25.3333C28 26.0406 27.719 26.7189 27.219 27.219C26.7189 27.719 26.0406 28 25.3333 28H6.66667C5.95942 28 5.28115 27.719 4.78105 27.219C4.28095 26.7189 4 26.0406 4 25.3333V6.66667ZM22.6 11.1747C22.85 11.4247 22.9904 11.7638 22.9904 12.1173C22.9904 12.4709 22.85 12.81 22.6 13.06L15.0573 20.6027C14.8073 20.8526 14.4682 20.993 14.1147 20.993C13.7611 20.993 13.422 20.8526 13.172 20.6027L9.4 16.832C9.14981 16.5818 9.00926 16.2425 9.00926 15.8887C9.00926 15.5348 9.14981 15.1955 9.4 14.9453C9.65019 14.6951 9.98951 14.5546 10.3433 14.5546C10.6972 14.5546 11.0365 14.6951 11.2867 14.9453L14.1133 17.7747L20.7133 11.1747C20.8372 11.0507 20.9842 10.9524 21.1461 10.8853C21.3079 10.8182 21.4814 10.7836 21.6567 10.7836C21.8319 10.7836 22.0054 10.8182 22.1673 10.8853C22.3291 10.9524 22.4762 11.0507 22.6 11.1747Z" />
    </svg>
  ),
};

// ── 오늘은 어떤 일부터 시작해볼까요? — 하위 항목 아이콘 그리드(0907 기획서 개편) ──────
// 텍스트 목록 대신 아이콘 버튼을 나열하고, 위 안내줄에 마우스오버한 항목의 설명을 보여준다.
// hover가 없을 때는 첫 번째 항목(subItems[0].text)이 기본 안내문구다(기획서 data-default와 동일).
function TaskIconGrid({
  subItems,
  onSelect,
}: {
  subItems: HomeTaskSubItem[];
  onSelect: (sub: HomeTaskSubItem) => void;
}) {
  const [hoverText, setHoverText] = useState<string | null>(null);

  return (
    <div className="mt-4 border-t border-border pt-4">
      <div className="flex items-center gap-2">
        <IconInfoCircle className="shrink-0 text-info-600" />
        {/* min-h 는 장식이 아니다 — 16px bold 안내문이 좁은 폭에서 2줄로 접히면, 높이를 예약해두지
            않을 경우 마우스오버할 때마다 아래 아이콘 그리드가 통째로 튄다(2.6em = 2 × lh 1.3). */}
        <span className="flex min-h-[2.6em] items-center text-body-md font-bold text-info-600">
          {hoverText ?? subItems[0].text}
        </span>
      </div>
      {/* 시안의 w-80 + gap-32 를 그대로 쓰면 카드 실폭(~397)에 4칸이 안 들어가 3칸으로 접힌다.
          w-1/4 로 4열을 폭과 무관하게 보장하고, justify-center 가 끝줄(7개→4+3)을 가운데 정렬한다. */}
      <div className="flex flex-wrap justify-start gap-y-4 pt-4">
        {subItems.map((sub) => (
          <button
            key={sub.text}
            type="button"
            onMouseEnter={() => setHoverText(sub.text)}
            onMouseLeave={() => setHoverText(null)}
            onClick={() => onSelect(sub)}
            className="flex w-1/4 flex-col items-center gap-2 rounded-xl py-2 text-center transition-colors hover:bg-group-subtle"
          >
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-group-subtle text-group">
              {sub.icon}
            </span>
            <span className="break-keep text-body-sm font-bold text-text-secondary">
              {sub.shortLabel[0]}<br />{sub.shortLabel[1]}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── 컴포넌트 ────────────────────────────────────────────────────────────────

export function HomePage() {
  const memberName = useAuthStore((s) => s.user?.memberName);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [taskOpen, setTaskOpen] = useState(true);
  const [siteOpen, setSiteOpen] = useState(true);
  const [regOpen, setRegOpen] = useState(false);
  const [detailSiteId, setDetailSiteId] = useState<number | null>(null);
  const [expandedSiteId, setExpandedSiteId] = useState<number | null>(null);

  // 오늘의 현장 상황(활동로그·알림) 바로가기 — resolveMenuCodeAction 공용 판정.
  // webMenuCode가 WEB_STUDIO 외 포털(WEB_PHOTO/WEB_SCHEDULE 등)에 등록된 경우도 찾아야 하므로
  // 전체 포털 조회를 쓴다.
  const { data: menuTree } = useAllMenuTree();

  // "오늘은 어떤 일부터 시작해볼까요?" 항목의 이동 경로 — TM_MENU(WEB_PHOTO/WEB_SCHEDULE/WEB_WORKLOG)에
  // MENU_CODE로 등록해둔 ROUTE_PATH를 우선 쓰고, 조회 실패·미시딩 시 HOME_TASKS의 하드코딩
  // path로 폴백한다(resolveHomeTaskPath).
  const { data: photoTaskMenus } = useMenuTree('WEB_PHOTO');
  const { data: scheduleTaskMenus } = useMenuTree('WEB_SCHEDULE');
  const { data: workLogTaskMenus } = useMenuTree('WEB_WORKLOG');
  const resolveHomeTaskPath = (agentId: HomeTask['agentId'], sub: HomeTaskSubItem): string => {
    const tree = agentId === 'photo-organizer' ? photoTaskMenus
      : agentId === 'scheduler' ? scheduleTaskMenus
      : agentId === 'work-log' ? workLogTaskMenus
      : undefined;
    const node = sub.menuCode ? findByCode(tree, sub.menuCode) : null;
    return node?.routePath ?? sub.path;
  };

  // 1) 인사말
  const greeting = useQuery({
    queryKey: ['studio', 'greeting'],
    queryFn: () => fetchGreeting(),
  });

  // 2) 오늘의 현장 상황 — 활성 현장(DONE 제외), 최근 활동순, 최대 10개
  // (모바일 앱과 정렬 기준 통일 — 앱 home_sites_provider.dart 참고)
  // 2026-10-12 개선사항 D-15 — '☑ 내 현장만'(기본: 직원 체크, 사장님 해제).
  // 체크 시 로그인 사용자가 현장 담당자 또는 관리자인 현장만(사장님도 동일), 해제 시 전체 현장.
  // 체크 상태·memberId 를 키에 넣는다(키 접두어 ['studio','active-sites'] 는 그대로 — 무효화 호환).
  const memberId = useAuthStore((s) => s.user?.memberId);
  const [mineOnly, setMineOnly] = useMineOnlyState();
  const mineApplied = mineOnly && memberId != null;
  const sites = useQuery({
    enabled: SHOW_SITE_STATUS,
    queryKey: ['studio', 'active-sites', mineApplied ? memberId : null],
    queryFn: () =>
      fetchSiteList({
        site_status_not: 'DONE', sort: 'last_access', dedupeSite: true, limit: 10,
        ...(mineApplied ? { memberIds: [memberId] } : {}),
      }),
  });

  const siteItems: SiteListItem[] = sites.data?.items ?? [];
  const siteIds = siteItems.map((s) => s.siteId);

  // 2-1) 오늘 작업공정/발주공정 — siteId 전체 배치 조회 1회(카드 개수와 무관)
  const siteTodays = useQuery({
    queryKey: ['studio', 'site-today', siteIds],
    queryFn: () => fetchSiteTodays(siteIds),
    enabled: SHOW_SITE_STATUS && siteIds.length > 0,
  });
  const siteTodayMap = new Map((siteTodays.data ?? []).map((d) => [d.siteId, d.content]));

  // 2-2) 최근2일 현장보고 — siteId 전체 배치 조회 1회, siteId로 그룹핑해서 카드별로 나눔
  const siteActivities = useQuery({
    queryKey: ['studio', 'site-activities', siteIds],
    queryFn: () => fetchSiteActivities(siteIds, WORK_LOG_AGENT_ID, { sinceDays: 2 }),
    enabled: SHOW_SITE_STATUS && siteIds.length > 0,
  });
  const siteActivityMap = groupBy(siteActivities.data ?? [], (a: SiteActivityItem) => a.siteId);

  // 2-3) 최근2일 중요 알림 — siteId 전체 배치 조회 1회, siteId로 그룹핑해서 카드별로 나눔
  const importantNotis = useQuery({
    queryKey: ['studio', 'important-notifications', siteIds, memberId],
    queryFn: () => fetchImportantNotifications(memberId!, siteIds, 2),
    enabled: SHOW_SITE_STATUS && siteIds.length > 0 && !!memberId,
  });
  const importantNotiMap = groupBy(importantNotis.data ?? [], (n) => n.siteId ?? -1);

  // 현장 카드 열 — 화살표로 3개씩 페이지 이동. clientWidth 기반 상대 스크롤은
  // 카드 폭 계산과 미세하게 어긋나 끝에서 잘리는 문제가 있었음 — 대신 실제 카드
  // DOM 위치(offsetLeft)를 측정해서 정확히 그 카드로 이동시키는 방식으로 교체.
  const siteRailRef = useRef<HTMLDivElement>(null);
  const siteCardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [sitePage, setSitePage] = useState(0);
  const SITE_PAGE_SIZE = 3;
  const totalSitePages = Math.max(1, Math.ceil(siteItems.length / SITE_PAGE_SIZE));

  const goToSitePage = (page: number) => {
    const clamped = Math.max(0, Math.min(page, totalSitePages - 1));
    setSitePage(clamped);
    const rail = siteRailRef.current;
    const firstCard = siteCardRefs.current[clamped * SITE_PAGE_SIZE];
    if (!rail || !firstCard) return;
    // 마지막 페이지는 카드가 3개 미만일 수 있어 offsetLeft가 끝을 넘어갈 수 있음 → 실제 최대치로 clamp
    const maxLeft = rail.scrollWidth - rail.clientWidth;
    rail.scrollTo({ left: Math.min(firstCard.offsetLeft, maxLeft), behavior: 'smooth' });
  };

  return (
    // L2 광폭 대시보드 — doc 스크롤 + xl(1440) 중앙 컬럼 + page padding 32 (D-07 §21.3~21.5).
    // 섹션 간격은 표준 Section Gap 32 대신 48(spacing-09) — 홈은 섹션 성격이 크게 갈려 사용자 요청분.
    <Page width="xl" className="space-y-09">

      {/* ── 1. 메인 타이틀 — 표준 페이지 머리(PageHeader). 부제 = 인사문구 ──────────── */}
      {/* 인사말 실패 시 부제를 그리지 않는 것이 의도된 동작이다(에러 처리 누락 아님).
          인사문구는 제목 옆 부가 인라인 텍스트라 상태 블록 대상이 아니고(§15.37 Don't),
          실패해도 사용자가 취할 행동이 없어 인라인 알림(§17.2)도 소음이 된다.
          조회 중에는 빈 줄로 높이를 잡아 문구가 들어올 때 아래가 밀리지 않게 한다(D-07 §14.1). */}
      <PageHeader
        title={memberName ? `${memberName}님 어서오세요` : '어서오세요'}
        subtitle={greeting.data?.message ?? (greeting.isLoading ? '\u00a0' : undefined)}
      />

      {/* ── 1-1. 오늘 챙길 일 배너 — 목업(정적 샘플, API 없음). 940 S14-1 ─────────── */}
      <TodayFocusBanner />

      {/* ── 2. 오늘의 현장 상황, 빠르게 확인해볼까요? — 홈에서 숨김(SHOW_SITE_STATUS) ──── */}
      {/* 헤더는 표준 접힘형 타이틀(CollapsibleSection §15.31) — 로컬 말풍선 헤더 대신 사용 */}
      {SHOW_SITE_STATUS && (
      <CollapsibleSection
        title="오늘의 현장 상황, 빠르게 확인해볼까요?"
        open={siteOpen}
        onToggle={() => setSiteOpen((v) => !v)}
      >
        {/* 섹션 부제 — sites 쿼리에서 파생되지 않는 정적 문구라 로딩·빈·에러 분기 바깥에 둔다.
            content 분기에만 넣으면 로드 성공 시 문구가 나타나며 레일이 밀려 CLS가 된다(D-07 §14.1). */}
        {/* 부제 + '☑ 내 현장만'(오른쪽) — 2026-10-12 개선사항 D-15 */}
        <div className="mb-05 flex flex-wrap items-center gap-x-md gap-y-sm">
          <p className="mr-auto text-body-md text-text-muted">
            바쁜 사장님을 위해, 꼭 챙겨야 할 것만 빠르게 확인할 수 있습니다.
          </p>
          <Tooltip tip="내가 현장 담당자 또는 현장 관리자인 현장만 보기">
            <Checkbox
              label="내 현장만"
              checked={mineOnly}
              onChange={(e) => setMineOnly(e.target.checked)}
            />
          </Tooltip>
        </div>
        {(
          // 상태 전환은 D-07 §17.1 — 요청 → Loading → 성공+데이터 Content / 성공+0건 Empty / 실패 Error.
          // 네 갈래를 한 체인으로 두어 로딩·빈 상태가 겹쳐 나오지 않게 한다(§3.2 #8).
          sites.isLoading ? (
            <div className="grid grid-cols-3 gap-4">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-64 rounded-xl" />
              ))}
            </div>
          ) : sites.isError ? (
            <ErrorState
              description="현장 목록을 불러오지 못했어요. 잠시 후 다시 시도해주세요."
              onRetry={() => void sites.refetch()}
            />
          ) : siteItems.length === 0 && mineApplied ? (
            <EmptyState
              title="내가 담당·관리하는 현장이 없어요"
              description="'내 현장만'을 해제하면 전체 현장을 볼 수 있어요"
            />
          ) : siteItems.length === 0 ? (
            <EmptyState
              title="진행 중인 현장이 없어요"
              description="현장을 등록하면 오늘의 작업·발주·현장보고를 여기서 한 번에 볼 수 있어요"
              action={
                // 빈 상태 액션은 primary 가 아니라 secondary — §15.1 버튼 역할 규칙의 명시적 예외(D-07 §14.2)
                <Button variant="secondary" onClick={() => setRegOpen(true)}>
                  ＋ 현장 만들기
                </Button>
              }
            />
          ) : (
            <div className="relative">
              {/* 현장 목록은 왔는데 카드 내용 배치 조회 3종 중 일부가 실패한 경우 —
                  카드 골격은 이미 서 있으므로 카드마다 상태 블록을 세우지 않고
                  섹션 위 인라인 알림 한 개로 알린다(§17.2 "부분·인라인 → 인라인 알림"). */}
              {(siteTodays.isError || siteActivities.isError || importantNotis.isError) && (
                <Alert kind="error" className="mb-4">
                  일부 현장 정보를 불러오지 못했어요. 잠시 후 다시 시도해주세요.
                </Alert>
              )}
              {/* 왼쪽 화살표 — 실제 카드 DOM 위치로 이동, 페이지 인덱스로 양끝 clamp */}
              <button
                type="button"
                aria-label="이전 현장 보기"
                onClick={() => goToSitePage(sitePage - 1)}
                disabled={sitePage === 0}
                className="absolute left-0 top-1/2 z-10 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-surface-raised text-text shadow-emphasis transition-opacity disabled:pointer-events-none disabled:opacity-0"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>

              {/* full-bleed 레일 — `-mx-page`(−32)로 border box 를 페이지 패딩 바깥까지 넓혀
                  클리핑 지점을 화면 가장자리로 밀고, `px-page`(+32)로 스크롤 콘텐츠에 같은 크기의
                  거터를 되돌려 첫/마지막 카드는 콘텐츠 기준선에 맞춘다. 순 오프셋 0이라 main 에
                  가로 스크롤이 생기지 않는다. */}
              <div
                ref={siteRailRef}
                className="-mx-page flex gap-4 overflow-x-auto px-page scroll-smooth"
                style={{ scrollbarWidth: 'none' }}
              >
              {siteItems.map((site, siteIdx) => {
                const today = siteTodayMap.get(site.siteId) ?? null;
                const activities = siteActivityMap.get(site.siteId) ?? [];
                const notis = importantNotiMap.get(site.siteId) ?? [];
                const issueCount = countIssues(activities, notis);
                return (
                <div
                  key={site.siteId}
                  ref={(el) => { siteCardRefs.current[siteIdx] = el; }}
                  className="w-[calc((100%-2rem)/3)] min-w-[380px] shrink-0"
                >
                {/* Card 는 .body 래퍼를 자체 생성하고 bodyClassName prop 이 없다 — 하단 더보기 버튼을
                    바닥에 붙이려면 그 .body 까지 flex 열로 만들어야 해서 자식 선택자로 건다. */}
                <Card className="flex h-full flex-col [&>.body]:flex [&>.body]:flex-1 [&>.body]:flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      {/* 배지가 이름 앞 — 상태를 이름 지나치지 않고 먼저 읽는다(시안 3836:1836) */}
                      <div className="flex items-center gap-2">
                        <Badge status={siteBadgeStatus(site.siteStatus)} className="shrink-0">
                          {site.siteStatusName ?? site.siteStatus}
                        </Badge>
                        <h3 className="truncate text-heading-md font-bold text-text">{site.customerName}</h3>
                      </div>
                      {/* `!` 2개 — `.app-card p`(components.css)가 size·color 둘 다 특이도로 이긴다 */}
                      <p className="mt-2 truncate !text-body-md !text-text-secondary">
                        {[site.bldgName, site.addressDong && `${site.addressDong}동`, site.addressHo && `${site.addressHo}호`].filter(Boolean).join(' · ') || site.siteAddress}
                      </p>
                    </div>
                    <button
                      type="button"
                      title="현장 상세/편집"
                      aria-label="현장 상세/편집"
                      onClick={() => setDetailSiteId(site.siteId)}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-text-muted transition-colors hover:text-group"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                  </div>
                  <IssueSummary issueCount={issueCount} />
                  {/* 이슈 표시줄과 아래 섹션 목록을 확실히 분리하는 굵은 구분선.
                      flex-1 은 안쪽 SiteStatusSections 가 남은 높이를 채워 하단 더보기 버튼의
                      mt-auto 가 바닥에 붙게 하기 위함이다(카드 3장의 버튼 높이 정렬). */}
                  <div className="mt-4 flex-1 border-t-2 border-border pt-4">
                    <SiteStatusSections
                      today={today}
                      activities={activities}
                      notis={notis}
                      limit={1}
                      navigate={navigate}
                      menuTree={menuTree}
                      onMoreClick={() => setExpandedSiteId(site.siteId)}
                    />
                  </div>
                </Card>
                </div>
                );
              })}
              </div>

              {/* 오른쪽 화살표 */}
              <button
                type="button"
                aria-label="다음 현장 보기"
                onClick={() => goToSitePage(sitePage + 1)}
                disabled={sitePage >= totalSitePages - 1}
                className="absolute right-0 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full border border-border bg-surface-raised text-text shadow-emphasis transition-opacity disabled:pointer-events-none disabled:opacity-0"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          )
        )}
      </CollapsibleSection>
      )}

      {/* ── 3. 오늘은 어떤 일부터 시작해볼까요? — 2번 섹션과 동일한 표준 접힘형 타이틀(§15.31).
          카드 3장은 하나의 선반으로 합치지 않고 각자 독립된 카드로 유지. */}
      <CollapsibleSection
        title="오늘은 어떤 일부터 시작해볼까요?"
        open={taskOpen}
        onToggle={() => setTaskOpen((v) => !v)}
      >
        <CardList layout="grid-fit" minColWidth={380}>
          {HOME_TASKS.filter((task) => !task.hidden).map((task) => (
            <div key={task.agentId} data-group={task.group} className="h-full">
              <Card className="flex h-full flex-col">
                {/* h3 인 이유 — `.app-card h4`(components.css)가 15px/600 + margin 을 특이도로 강제한다.
                    .app-card 는 h4 만 스타일하므로 h3 으로 비껴간다(현장 카드가 h5 를 쓴 것과 같은 회피). */}
                <div className="flex items-center gap-2">
                  <span className="shrink-0 text-group">
                    {TASK_AGENT_ICON[task.agentId]}
                  </span>
                  <h3 className="flex-1 text-heading-md font-bold text-text">
                    {task.label}
                  </h3>
                </div>
                {/* 아이콘 그리드는 0907 기획서 개편분 — 하위 항목 텍스트 목록 대신 아이콘+2줄 라벨,
                    위 안내줄이 마우스오버한 항목의 설명을 보여준다(TaskIconGrid). */}
                <TaskIconGrid
                  subItems={task.subItems}
                  onSelect={(sub) => navigate(resolveHomeTaskPath(task.agentId, sub))}
                />
              </Card>
            </div>
          ))}
        </CardList>
      </CollapsibleSection>

      {/* 현장 등록 팝업 — 빈 상태의 "＋ 현장 만들기" 진입점 (SitesListPage 와 동일 패턴) */}
      {regOpen && (
        <SiteRegistrationPopup
          agentId={0}
          onClose={() => setRegOpen(false)}
          onComplete={() => {
            queryClient.invalidateQueries({ queryKey: ['studio', 'active-sites'] });
            setRegOpen(false);
          }}
        />
      )}

      {/* 현장 상세 팝업 */}
      {detailSiteId !== null && (
        <SiteDetailPanel siteId={detailSiteId} onClose={() => setDetailSiteId(null)} />
      )}

      {/* 현장카드 "더보기" — 오늘 작업/발주 공정 + 현장보고·중요알림 전체 목록 */}
      {expandedSiteId !== null && (() => {
        const expandedSite = siteItems.find((s) => s.siteId === expandedSiteId);
        if (!expandedSite) return null;
        const expandedActivities = siteActivityMap.get(expandedSite.siteId) ?? [];
        const expandedNotis = importantNotiMap.get(expandedSite.siteId) ?? [];
        return (
          <Modal open onClose={() => setExpandedSiteId(null)} title={`${expandedSite.customerName} 님`}>
            <div className="mb-4 flex items-center gap-2">
              <p className="text-body-sm text-text-secondary">
                {[expandedSite.bldgName, expandedSite.addressDong && `${expandedSite.addressDong}동`, expandedSite.addressHo && `${expandedSite.addressHo}호`].filter(Boolean).join(' · ') || expandedSite.siteAddress}
              </p>
              <Badge status={siteBadgeStatus(expandedSite.siteStatus)} className="shrink-0">
                {expandedSite.siteStatusName ?? expandedSite.siteStatus}
              </Badge>
            </div>
            <IssueSummary issueCount={countIssues(expandedActivities, expandedNotis)} />
            {/* 헤더(주소·배지·이슈요약)는 고정, 아래 4섹션 내용만 뷰포트 넘으면 내부 스크롤 — 모달 자체가
                overflow:hidden이라 이 래퍼 없이는 화면 밖으로 넘친 내용이 그냥 잘려서 안 보이게 된다. */}
            <div className="mt-4 max-h-[60vh] overflow-y-auto border-t-2 border-border pt-4">
              <SiteStatusSections
                today={siteTodayMap.get(expandedSite.siteId) ?? null}
                activities={expandedActivities}
                notis={expandedNotis}
                navigate={navigate}
                menuTree={menuTree}
              />
            </div>
          </Modal>
        );
      })()}

    </Page>
  );
}
